import { useTranslation } from 'react-i18next';
import { useNavigationHistoryStore } from '../../hooks/useNavigationHistoryStore';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { useSAPDataStore } from '../../store/SAPDataStore';
import { MachineCard } from '../MachineCard';
import LineCard from '../LineCard';
import { type Machine } from '../../services/BackendDataService';
import { useEffect } from 'react';
import { useThemeClasses } from '../../providers/ThemeProvider';

// Simple cx utility for combining class names
const cx = (...classes: (string | undefined | null | false)[]): string => {
  return classes.filter(Boolean).join(' ');
};

interface RecentlyViewedTabProps {
  // No props needed - using centralized store
}

const RecentlyViewedTab = ({}: RecentlyViewedTabProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { history, loading, refresh } = useNavigationHistoryStore();
  const navigate = useNavigate();
  const { getMachineById, getLineById } = useSAPDataStore();
  const themeClasses = useThemeClasses();

  // Load history on mount and when user becomes active
  useEffect(() => {
    if (!user?.username) return;
    
    refresh();
    
    // Refresh when tab becomes visible (user switches back to tab)
    const handleVisibilityChange = () => {
      if (!document.hidden && user?.username) {
        refresh();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user?.username, refresh]); // Now safe to include refresh as it's stable

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <div className="w-8 h-8 border-4 border-blue-100 dark:border-blue-900 border-t-blue-500 rounded-full animate-spin"></div>
        <span className={cx(themeClasses.textSecondary, "text-sm")}>{t('loading')}</span>
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4 text-center">
        <div className="text-4xl mb-2">📋</div>
        <h3 className={cx(themeClasses.textPrimary, "text-lg font-semibold")}>{t('noHistoryYet') || 'No recent visits'}</h3>
        <p className={cx(themeClasses.textSecondary, "text-sm max-w-md")}>{t('startExploring') || 'Start exploring machines and lines to see them here!'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className={cx(themeClasses.textPrimary, "text-lg font-semibold")}>{t('recentlyViewed') || 'Recently Viewed'}</h3>
        <button
          onClick={() => refresh()}
          disabled={loading}
          className={cx(
            "flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors",
            loading 
              ? cx(themeClasses.textMuted, "cursor-not-allowed opacity-50")
              : cx(themeClasses.textPrimary, themeClasses.hover, "hover:bg-blue-50 dark:hover:bg-blue-900/20")
          )}
          title={t('refresh') || 'Refresh'}
        >
          <svg 
            className={cx(
              "w-4 h-4",
              loading ? "animate-spin" : ""
            )} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <span>{loading ? (t('refreshing') || 'Refreshing...') : (t('refresh') || 'Refresh')}</span>
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {history.slice(0, 12).map((event, index) => {
          const date = new Date(event.timestamp);
          const isToday = date.toDateString() === new Date().toDateString();
          const timeStr = isToday 
            ? date.toLocaleTimeString() 
            : date.toLocaleDateString();

          // For machine entries, use MachineCard component
          if (event.type === 'machine') {
            const machineDetails = getMachineById(event.id);
            if (machineDetails) {
              const handleMachineClick = (machine: Machine) => {
                navigate(`/machine/${machine.maschinennummer || machine.maschineBK}`, { 
                  state: {
                    machineId: machine.maschinennummer || machine.maschineBK,
                    machineName: machine.machineName || machine.bautyp || event.id,
                    lineId: machine.lineId || '',
                    machineType: machine.bautyp || '',
                    factory: machine.factoryName || '1201 (Factory Name)'
                  }
                });
              };

              return (
                <div key={`${event.timestamp}-${index}`} className="relative">
                  <MachineCard
                    machine={machineDetails}
                    onClick={handleMachineClick}
                  />
                  {/* Recent view timestamp overlay */}
                  <div className="absolute bottom-2 left-2 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                    {timeStr}
                  </div>
                </div>
              );
            }
          }

          // For line entries, use LineCard component if details found
          if (event.type === 'line') {
            const lineDetails = getLineById(event.id);
            if (lineDetails) {
              const handleLineClick = (line: any) => {
                navigate(`/line/${line.linieBK}`, { state: {
                  lineId: line.linieBK,
                  lineName: line.linieName,
                  factory: line.factoryName || '1201 (Factory Name)',
                  machineCount: line.maschinen?.length?.toString() || '0',
                  lineType: 'Production Line',
                  supervisor: 'John Smith'
                }});
              };
              return (
                <div key={`${event.timestamp}-${index}`} className="relative">
                  <LineCard
                    line={lineDetails}
                    onClick={handleLineClick}
                  />
                  {/* Recent view timestamp overlay */}
                  <div className="absolute bottom-2 left-2 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                    {timeStr}
                  </div>
                </div>
              );
            }
          }

          // Fallback: original card format
          return (
            <div
              key={`${event.timestamp}-${index}`}
              className={cx(
                themeClasses.cardBg, 
                themeClasses.cardBorder, 
                "p-4 rounded-lg border cursor-pointer transition-all duration-200 hover:shadow-md",
                themeClasses.hover
              )}
              onClick={() => {
                if (event.type === 'machine') {
                  const machineDetails = getMachineById(event.id);
                  navigate(`/machine/${event.id}`, { state: {
                    machineId: event.id,
                    machineName: event.name || machineDetails?.machineName || machineDetails?.bautyp || event.id,
                    lineId: machineDetails?.lineId || '',
                    machineType: machineDetails?.bautyp || '',
                    factory: machineDetails?.factoryName || '1201 (Factory Name)'
                  }});
                } else if (event.type === 'line') {
                  const lineDetails = getLineById(event.id);
                  navigate(`/line/${event.id}`, { state: {
                    lineId: event.id,
                    lineName: event.name || lineDetails?.linieName || event.id,
                    factory: lineDetails?.factoryName || '1201 (Factory Name)',
                    machineCount: lineDetails?.maschinen.length.toString() || '0',
                    lineType: 'Production Line',
                    supervisor: 'John Smith'
                  }});
                }
              }}
            >
              <div className="flex items-start space-x-3">
                <div className={cx(
                  "w-3 h-3 rounded-full flex-shrink-0 mt-1",
                  event.type === 'machine' 
                    ? "bg-blue-500" 
                    : "bg-green-500"
                )} />
                <div className="flex-1 min-w-0">
                  <div className={cx(themeClasses.textPrimary, "font-medium text-sm truncate")}>
                    {event.name || `${event.type} ${event.id}`}
                  </div>
                  <div className={cx(themeClasses.textSecondary, "flex items-center space-x-2 text-xs mt-1")}>
                    <span>{event.type === 'machine' ? '🔧' : '🏭'}</span>
                    <span className="truncate">{event.id}</span>
                    <span>•</span>
                    <span className="flex-shrink-0">{timeStr}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RecentlyViewedTab;
